/**
 * Fetches bulk-self-schedule interview status for the prerequisite overlay.
 * GET /api_ats/v1/ats_applications/bulk/interview_status
 *
 * Returns the candidate set + the list of stage actions on the job
 * stage + the no-email bucket. The shape is identical to the legacy
 * `ScheduleAPI.getStageActionDetails` consumer — we keep the legacy
 * call site working until JobCandidates flips behind
 * `feBulkSelfSchedule`. See `categorise.ts` for the rule set that
 * turns this response into eligible / no-email / already-scheduled.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/request';
import type { BulkSelfScheduleInterviewStatusResponse } from './types';

export interface UseBulkSelfScheduleInterviewStatusParams {
  selectedApplicationIds?: number[];
  unSelectedApplicationIds?: number[];
  jobStageId: number;
  jobStatus?: string;
}

async function fetchBulkSelfScheduleInterviewStatus(
  params: UseBulkSelfScheduleInterviewStatusParams
): Promise<BulkSelfScheduleInterviewStatusResponse> {
  const {
    selectedApplicationIds,
    unSelectedApplicationIds,
    jobStageId,
    jobStatus,
  } = params;
  return api.get<BulkSelfScheduleInterviewStatusResponse>(
    'ats_applications/bulk/interview_status',
    {
      params: {
        ats_application_ids: !unSelectedApplicationIds
          ? selectedApplicationIds
          : undefined,
        ats_job_stage_id: jobStageId,
        bulk_selected_ats_job_stage_ids: unSelectedApplicationIds && [
          jobStageId,
        ],
        bulk_selected_application_statuses: unSelectedApplicationIds &&
          jobStatus && [jobStatus],
        bulk_exempted_application_ids: unSelectedApplicationIds,
      },
    }
  );
}

export function useBulkSelfScheduleInterviewStatus(
  params: UseBulkSelfScheduleInterviewStatusParams,
  options?: { enabled?: boolean }
): UseQueryResult<BulkSelfScheduleInterviewStatusResponse, Error> {
  return useQuery({
    queryKey: [
      'schedule-interview',
      'bulk-self-schedule-interview-status',
      params.selectedApplicationIds,
      params.unSelectedApplicationIds,
      params.jobStageId,
      params.jobStatus,
    ],
    queryFn: () => fetchBulkSelfScheduleInterviewStatus(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
  });
}
